import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageCTA from "@/components/feature/PageCTA";
import AnimatedSection from "@/components/AnimatedSection";
import { logoUrl, astrojalGradientText } from "@/mocks/homeData";

const aboutValues = [
  {
    icon: "ri-code-s-slash-line",
    title: "Laravel-First Engineering",
    desc: "Laravel is at the heart of our backend engineering. We build secure, scalable and maintainable applications around it.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Quality & Security",
    desc: "Clean architecture, comprehensive testing and security best practices are non-negotiable in everything we deliver.",
  },
  {
    icon: "ri-chat-3-line",
    title: "Transparent Communication",
    desc: "Clear updates, honest estimates and close collaboration with your team throughout every phase of the project.",
  },
  {
    icon: "ri-arrow-right-up-line",
    title: "Built to Scale",
    desc: "We design systems that grow with your users, your data and your business — not just for launch day.",
  },
];

const aboutStats = [
  { value: "10+", label: "Years of Experience" },
  { value: "100+", label: "Projects Delivered" },
  { value: "50+", label: "Clients Worldwide" },
  { value: "100%", label: "Commitment to Quality" },
];

const aboutApproach = [
  { number: "01", title: "Understand", desc: "We start by understanding your business, users and goals before writing any code." },
  { number: "02", title: "Architect", desc: "We design clean, scalable architecture and choose the right technology for your needs." },
  { number: "03", title: "Build & Test", desc: "We develop in focused sprints with thorough testing at every stage." },
  { number: "04", title: "Ship & Support", desc: "We deploy to reliable infrastructure and support you long after launch." },
];

const aboutTechs = [
  { name: "Laravel", icon: "ri-code-s-slash-line", color: "text-red-500" },
  { name: "PHP", icon: "ri-code-box-line", color: "text-purple-500" },
  { name: "Vue.js", icon: "ri-vuejs-line", color: "text-green-500" },
  { name: "React", icon: "ri-reactjs-line", color: "text-blue-400" },
  { name: "Flutter", icon: "ri-flutter-line", color: "text-cyan-400" },
  { name: "MySQL", icon: "ri-database-2-line", color: "text-blue-600" },
  { name: "Redis", icon: "ri-server-line", color: "text-red-400" },
  { name: "AWS", icon: "ri-cloud-line", color: "text-orange-400" },
  { name: "Docker", icon: "ri-ship-line", color: "text-blue-500" },
  { name: "REST APIs", icon: "ri-exchange-line", color: "text-accent-500" },
];

export default function AboutUsPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "About Us" },
        ]}
      />

      {/* Hero */}
      <section className="relative py-20 md:py-24 lg:py-28 bg-hero-gradient overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")` }} />
        <div className="container-astro relative z-10 text-center">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
              <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">About AstroJal</span>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-background-50 leading-[1.1] mb-6 font-heading tracking-tight">
              Laravel Experts Building Scalable Digital Products
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <p className="text-base md:text-lg text-background-400 leading-relaxed mb-8 max-w-2xl mx-auto">
              AstroJal Technology is a software engineering company specializing in Laravel, web applications, SaaS platforms, APIs and modern cloud infrastructure — helping businesses turn ideas into reliable digital products.
            </p>
          </AnimatedSection>
          <AnimatedSection delay={300}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/services" className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group">
                Explore Our Services
                <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a href="/contact-us" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white text-sm font-semibold rounded-xl hover:bg-white/10 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover group">
                Let&apos;s Talk
                <i className="ri-chat-1-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </AnimatedSection>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
      </section>

      {/* Who We Are */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <AnimatedSection>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-accent-500/5 to-secondary-500/5 rounded-3xl" />
                <div className="relative rounded-2xl overflow-hidden border border-background-200/60">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20software%20engineering%20team%20collaborating%20around%20a%20large%20desk%20with%20laptops%20and%20code%20on%20screens%2C%20warm%20natural%20lighting%2C%20clean%20premium%20office%20environment%2C%20professional%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=astrojal-about-team&orientation=landscape"
                    alt="AstroJal software engineering team"
                    className="w-full h-[320px] md:h-[400px] object-cover object-top"
                  />
                </div>
              </div>
            </AnimatedSection>
            <div>
              <AnimatedSection delay={100}>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                  <span className="text-xs font-semibold text-accent-600 tracking-wide uppercase">Who We Are</span>
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                  A Software Engineering Company Focused on Long-Term Value
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  We are a team of senior engineers, designers and project leads who build modern digital products using proven technology. Our core expertise is Laravel and PHP, complemented by Vue.js, React, Flutter, cloud infrastructure and AI integration.
                </p>
                <p className="text-base text-foreground-600 leading-relaxed mb-8">
                  Whether you&apos;re launching an MVP, scaling a SaaS platform or modernizing a legacy system, we deliver clean, secure and maintainable software engineered to perform.
                </p>
              </AnimatedSection>

              <AnimatedSection delay={200}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                  {aboutStats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className={`text-2xl md:text-3xl font-extrabold font-heading bg-clip-text text-transparent ${astrojalGradientText}`}>
                        {stat.value}
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                What We Stand For
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {aboutValues.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                How We Work
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                A clear, structured process that keeps your project on track from first idea to long-term support.
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {aboutApproach.map((step, i) => (
              <AnimatedSection key={step.number} delay={i * 100}>
                <div className="group relative p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F97316] mb-4 font-heading">
                    {step.number}
                  </span>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2 font-heading">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                Technologies We Master
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {aboutTechs.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <a href="/technologies" className="flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift h-full">
                  <span className={`w-10 h-10 flex items-center justify-center ${tech.color}`}>
                    <i className={`${tech.icon} text-2xl`} />
                  </span>
                  <span className="text-sm font-semibold text-foreground-800">{tech.name}</span>
                </a>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={200}>
            <div className="text-center mt-10">
              <a href="/technologies" className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors btn-arrow-hover group">
                View Full Technology Stack
                <i className="ri-arrow-right-line btn-arrow text-xs" />
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <PageCTA
        heading="Let&apos;s Build Something That Scales"
        text="Have an idea, an existing application or a complex Laravel project? Talk to our experts and let's turn your requirements into a reliable digital product."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}